import { mapActions, mapGetters, mapState } from "vuex";
import {
  ACTIVITY_TYPE_SELECTION,
  LESSON_TEMPLATE_TYPE,
  NAMESPACE,
  VALIDATION,
} from "@modules/editor/service/editor.constant";
import { LESSON_ACTIVITY_TYPE } from "@plugins/common/constant/resource.constant";
import { appLocalStorage } from "@utils/app.storage";
import { FROM_EDITOR_PAGE_LOCATION } from "@modules/assignment/service/assignment.constant";
import { isEmpty } from "@utils/var.check";
import EditorAddActivityPostHandlerMixin from "@modules/editor/mixin/editor.add.activity.post.handler.mixin";
import EditorAddSectionPostHandlerMixin from "@modules/editor/mixin/editor.add.section.post.handler.mixin";
import ErrorMixin from "@plugins/common/mixin/error.mixin";
import PageDirtyMixin from "@modules/app/mixin/page.dirty.mixin";
import EditorNavigationMixin from "@modules/editor/mixin/editor.navigation.mixin";
import ProcessingStateMixin from "@modules/app/mixin/processing.state.mixin";

export default {
  mixins: [
    EditorAddActivityPostHandlerMixin,
    EditorAddSectionPostHandlerMixin,
    EditorNavigationMixin,
    PageDirtyMixin,
    ProcessingStateMixin,
    ErrorMixin,
  ],
  data() {
    return {
      // selection mode: "", "SECTION", "ACTIVITY"
      selectionMode: "",
      activitiesSelected: [],
      sectionsSelected: [],
      // used for lock service
      lessonTitle: "",
      packageType: "",
      resourceUuid: "",
      selectionValidated: false,
    };
  },
  computed: {
    ...mapState(NAMESPACE.LESSON, {
      lesson: (state) => state.lesson,
    }),
    ...mapGetters(NAMESPACE.AUTH, ["userId"]),
    ...mapGetters(NAMESPACE.LESSON, [
      "getActivity",
      "getActivityList",
      "getSection",
      "getSectionList",
    ]),
    currentSectionId() {
      return this.params?.sectionId ?? null;
    },
    isDefaultSelectionMode() {
      return isEmpty(this.selectionMode);
    },
    addFromEditorLocation() {
      return this.params?.addFromEditorLocation ?? null;
    },
    fromCover() {
      return this.addFromEditorLocation === FROM_EDITOR_PAGE_LOCATION.COVER;
    },
    fromSectionCover() {
      return this.addFromEditorLocation === FROM_EDITOR_PAGE_LOCATION.SECTION_COVER;
    },
    fromActivity() {
      return this.addFromEditorLocation === FROM_EDITOR_PAGE_LOCATION.ACTIVITY;
    },
    inLessonEditor() {
      return (
        this.addFromEditorLocation === FROM_EDITOR_PAGE_LOCATION.COVER ||
        this.addFromEditorLocation === FROM_EDITOR_PAGE_LOCATION.SECTION_COVER ||
        this.addFromEditorLocation === FROM_EDITOR_PAGE_LOCATION.ACTIVITY
      );
    },
    redirectAfterCreated() {
      return this.params?.redirectAfterCreated ?? true;
    },
  },
  methods: {
    ...mapActions(NAMESPACE.LESSON, [
      "setEditingComponentId",
      "copySectionsToLesson",
      "copyPagesToSection",
      "copyPagesToNewFirstSection",
    ]),
    ...mapActions(NAMESPACE.COMPONENT, ["setEditingResourceUuid"]),
    handleActivitiesSelected(selected) {
      this.activitiesSelected = selected;
      if (this.activitiesSelected.length === 0) {
        this.removeDirty(this.dirtyId);
      } else {
        this.setDirty(this.dirtyId);
      }
    },
    handleSectionsSelected(selected) {
      this.sectionsSelected = selected;
      if (this.sectionsSelected.length === 0) {
        this.removeDirty(this.dirtyId);
      } else {
        this.setDirty(this.dirtyId);
      }
    },
    addSectionsOrActivities() {
      if (this.isDefaultSelectionMode) return;
      if (this.selectionMode === LESSON_ACTIVITY_TYPE.SECTION) {
        this.validateAddSections();
        if (this.selectionValidated) this.handleAddSections();
      } else {
        this.validateAddActivities();
        if (this.selectionValidated) this.handleAddActivities();
      }
    },
    validateAddSections() {
      if (this.getSectionList.length + this.sectionsSelected.length > VALIDATION.MAX_SECTION) {
        if (this.selectionValidated) this.selectionValidated = false;
        return this.handleMaxValidationError();
      }
      this.selectionValidated = true;
    },
    handleAddSections() {
      // Introduction 	Create new Section after Introduction
      // Section cover 	Create New Section after current Section
      // Activity	      Create New Section after current Section
      let targetPageIndex = 1;
      if (!this.fromCover) {
        const section = this.getSection(this.currentSectionId);
        targetPageIndex = section.pageNo + 1;
      }

      const templateLessonUuid = this.params.lesson
        ? this.params.lesson.uuid
        : this.paramsData.uuid;

      const requestData = {
        targetLessonUuid: this.lesson.uuid,
        lessonUuid: templateLessonUuid, // source lesson uuid (template's lesson uuid)
        lessonStatus: this.lesson.status, // target lesson status
        lessonActivityId: this.sectionsSelected,
        targetSectionId: null,
        targetPageIndex: targetPageIndex,
        remainTitle: true,
      };

      this.copySectionsToLesson(requestData).then(
        (response) => {
          this.setLastUsedTemplateLocalStorage();
          this.handlePostSaveSections(response);
        },
        (error) => {
          this.onAddSectionsOrActivitiesFailure(error);
        },
      );
    },
    setLastUsedTemplateLocalStorage() {
      if (this.params?.templateType ?? "" === LESSON_TEMPLATE_TYPE.MY_TEMPLATE) {
        appLocalStorage.setItem(
          `lastUsedMyTemplateUuid-${this.userId}`,
          this.template?.uuid ?? this.paramsData.uuid,
        );
      } else if (this.params?.templateType ?? "" === LESSON_TEMPLATE_TYPE.SLS_TEMPLATE) {
        appLocalStorage.setItem(
          `lastUsedSlsTemplateUuid-${this.userId}`,
          this.template?.uuid ?? this.paramsData.uuid,
        );
      }
    },
    validateAddActivities() {
      if (
        this.fromCover &&
        (this.getSectionList.length >= VALIDATION.MAX_SECTION ||
          this.activitiesSelected.length > VALIDATION.MAX_ACTIVITIES)
      ) {
        if (this.selectionValidated) this.selectionValidated = false;
        return this.handleMaxValidationError();
      } else if (!this.fromCover) {
        const activityList = this.getActivityList(this.currentSectionId);
        if (this.activitiesSelected.length + activityList.length > VALIDATION.MAX_ACTIVITIES) {
          if (this.selectionValidated) this.selectionValidated = false;
          return this.handleMaxValidationError();
        }
      }
      this.selectionValidated = true;
    },
    handleAddActivities() {
      // Introduction 	Create new Activity in new Section
      // Section cover	Create Activity after current Section cover
      // Activity	      Create Activity after current Activity
      const targetPageIndex = this.fromActivity ? this.params.targetPageIdx : 1;

      const templateLessonUuid = this.params.lesson
        ? this.params.lesson.uuid
        : this.paramsData.uuid;

      const requestData = {
        targetLessonUuid: this.lesson.uuid,
        lessonUuid: templateLessonUuid, // source lesson uuid (template uuid)
        lessonStatus: this.lesson.status, // target lesson status
        lessonActivityId: this.activitiesSelected,
        targetSectionId: this.fromCover ? null : this.currentSectionId,
        targetPageIndex: targetPageIndex,
        remainTitle: true,
      };

      const promise = this.fromCover ? this.copyPagesToNewFirstSection : this.copyPagesToSection;
      promise(requestData).then(
        (section) => {
          this.setLastUsedTemplateLocalStorage();
          if (this.fromCover) {
            // navigate to new section
            this.handlePostSaveSections(section);
          } else {
            // navigate to new activity
            this.handlePostSaveActivities(targetPageIndex);
          }
        },
        (error) => {
          this.onAddSectionsOrActivitiesFailure(error);
        },
      );
    },
    handlePostSaveSections(section) {
      const showCover = this.lesson.showCover;
      const showSectionCover = this.lesson.showSectionCover;
      if (this.getSectionList.length > 1 && (!showCover || !showSectionCover)) {
        this.postAddSectionToast(showCover, showSectionCover);
      }
      if (this.redirectAfterCreated) {
        this.goToSectionCover(section.id).then(() => {
          this.setEditingComponentId(section.id);
          this.setEditingResourceUuid(section.uuid);
        });
      } else {
        this.actionBack();
        this.$nextTick(() => {
          if (
            typeof this.params.addResourceActionBack !== "undefined" &&
            typeof this.params.addResourceActionBack === "function"
          ) {
            this.params.addResourceActionBack();
          }
        });
      }
    },
    handlePostSaveActivities(targetPageIndex) {
      const updatedActivityLists = this.getActivityList(this.params.sectionId);

      const showCover = this.lesson.showCover;
      const showSectionCover = this.lesson.showSectionCover;
      if (this.getSectionList.length > 1 && (!showCover || !showSectionCover)) {
        this.postAddSectionToast(showCover, showSectionCover);
      } else if (updatedActivityLists.length > 1 && !showCover) {
        this.postAddActivityToast();
      }

      if (this.redirectAfterCreated) {
        const nextTargetPage = updatedActivityLists.find((activity) => {
          return activity.pageNo === targetPageIndex;
        });

        // route to first newly added activities
        this.postAddActivityHandlerEditorVersion({
          newActivityId: nextTargetPage.id,
          selectedActivityType: ACTIVITY_TYPE_SELECTION.FROM_EXISTING,
        });
      } else {
        this.actionBack();
        this.$nextTick(() => {
          if (
            typeof this.params?.addResourceActionBack !== "undefined" &&
            typeof this.params?.addResourceActionBack === "function"
          ) {
            this.params.addResourceActionBack();
          }
        });
      }
    },
    getMaxSelectedErrorMessage() {
      return this.selectionMode === LESSON_ACTIVITY_TYPE.SECTION
        ? this.$i18n.t("lesson_editor_maximum_sections_error")
        : this.$i18n.t("lesson_editor_maximum_activities_error");
    },
    onAddSectionsOrActivitiesFailure(error) {
      this.resetProcessing();
      if (error[0]) {
        this.onFail();
        return this.passiveApiFailHandler();
      }
    },
  },
};
